import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import {
  createFolderEffect,
  deleteFolderEffect,
  folderDetailQueryKey,
  FOLDERS_STALE_TIME_MS,
  foldersQueryKey,
  type FolderRow,
  getFolderEffect,
} from '@listening/frontend/query/folders';
import { invalidateAudioCaches } from '@listening/frontend/query/audio-list';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Effect } from 'effect';
import { toast } from 'sonner';
import * as m from '@/paraglide/messages';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
} from '@/lib/audio-chunk-player';
import {
  type AddAudiosToFolderInput,
  addAudiosToFolderEffect,
  markAudiosFiled,
  moveAudioToFolderEffect,
  reconcileFolderAssignment,
  type RenameFolderInput,
  renameFolderEffect,
  useFoldersCollection,
  useFoldersListQuery,
  useMoveAudioToFolderLifecycle,
  useRenameFolderLifecycle,
} from '@listening/frontend/query/folders-collection';
import { rootLogger } from '@/lib/root-logger';
import { clientRuntime } from '@/runtimes/client-runtime';
import { eq } from '@/runtimes/effect-query';
import { audioService } from './audio-service';

const moduleLogger = rootLogger.child({
  module: 'FolderService',
});

// Non-suspense list read (used on the files route and the move-to-folder
// dropdown). `data` is always an array so consumers don't re-default.
const useListQuery = () => {
  const query = useFoldersListQuery();
  return {
    data: (query.data ?? []) as ReadonlyArray<FolderRow>,
    isPending: query.isPending,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

// FolderDetail has `audio_conversion_ids` + `child_folder_ids` that the
// summary-shaped list row doesn't carry. Per-id fetch via eq.queryOptions
// against a separate cache key; the files/$folderId route loader prefetches it
// (with a typed-404 → redirect) and the component reads it via useSuspenseQuery.
export const folderDetailQueryOptions = (folderId: string) =>
  eq.queryOptions({
    queryKey: folderDetailQueryKey(folderId),
    queryFn: () => getFolderEffect(folderId),
    staleTime: FOLDERS_STALE_TIME_MS,
  });

const useCreateMutation = () => {
  const foldersCollection = useFoldersCollection();
  const queryClient = useQueryClient();
  return useMutation({
    // Always clientside — run the body on the client runtime as an Effect.
    mutationFn: (input: {
      folderName: string;
      audioConversionIds: ReadonlyArray<string>;
    }) =>
      clientRuntime.runPromise(
        Effect.gen(function* () {
          // Server assigns the folder id, so no optimistic insert. After
          // create, refetch the folders collection — audio items that moved
          // into the new folder need their folder_id refreshed. Translates the
          // camelCase consumer shape (legacy compat) to the snake_case API
          // shape.
          const folder = yield* createFolderEffect({
            path: input.folderName.split('/'),
            audio_conversion_ids:
              input.audioConversionIds.length > 0
                ? input.audioConversionIds
                : null,
          });
          yield* Effect.promise(() => foldersCollection.utils.refetch());
          if (input.audioConversionIds.length > 0) {
            yield* Effect.promise(() => invalidateAudioCaches(queryClient));
          }
          return folder;
        }),
      ),
    onSuccess: () => {
      toast.success(m.toast_folder_created());
    },
  });
};

// Rename (path). Always clientside. Optimistic across BOTH the folders list
// cache (drives the /files index) AND the per-folder detail cache (drives the
// name shown on the /files/$folderId route). We intentionally do NOT route
// through the optimistic-list-entity helper here: its `update()` requires the
// row to already be in the list cache and throws on a miss — but the
// /files/$folderId loader only prefetches folder *detail*, so on a fresh load
// the list cache is cold and rename would throw, sending no PATCH and silently
// no-op'ing. The helper also only touches the list cache, leaving the detail
// cache (the actual source of the displayed name) stale even on success. The
// dual-cache choreography now lives in `@listening/frontend` (shared with
// mobile); the app only runs the mutationFn on its client runtime.
const useUpdateMutation = () => {
  return useMutation({
    mutationFn: (input: RenameFolderInput) =>
      clientRuntime.runPromise(renameFolderEffect(input)),
    ...useRenameFolderLifecycle(),
  });
};

const useDeleteMutation = () => {
  const foldersCollection = useFoldersCollection();
  const queryClient = useQueryClient();
  const loadedAudioId = useAudioChunkSelector((s) => s.audioId);
  const { unloadAudio, player } = useAudioChunkPlayer();
  const navigate = useNavigate();
  const matchRoute =
    audioService.utils.useMatchIsReadAlongScreenActiveForAudio();

  return useMutation({
    // Always clientside — run the body on the client runtime as an Effect.
    mutationFn: (opts: { folderId: string; recursiveDeleteItems: boolean }) =>
      clientRuntime.runPromise(
        Effect.gen(function* () {
          // Recursive delete also removes audio items — bypass the collection's
          // generic onDelete (which always calls non-recursive) and call the
          // Effect program directly. After success, refetch the folders
          // collection + invalidate the audio caches so deleted audios
          // disappear from list views.
          const resp = yield* deleteFolderEffect(
            opts.folderId,
            opts.recursiveDeleteItems,
          );
          yield* Effect.promise(() => foldersCollection.utils.refetch());
          yield* Effect.promise(() => invalidateAudioCaches(queryClient));
          return resp;
        }),
      ),
    onSuccess: (resp, opts) => {
      moduleLogger.info(
        `Successfully deleted folder ${opts.folderId} and ${opts.recursiveDeleteItems ? 'all files' : 'moved files to home'}`,
      );
      toast.success(
        opts.recursiveDeleteItems
          ? m.toast_removed_folder_and_files({
              count: resp.deleted_conversion_ids.length,
            })
          : m.toast_removed_folder(),
      );
      if (opts.recursiveDeleteItems) {
        const cleanup = (audioId: string) => {
          if (loadedAudioId === audioId) {
            unloadAudio();
            player.stop();
          }
          return matchRoute({ id: audioId });
        };

        if (resp.deleted_conversion_ids.map(cleanup).some(Boolean)) {
          moduleLogger.info('Navigating to home screen from deleted audio');
          return navigate({
            to: '/app/home',
          });
        }
      }
    },
  });
};

// Cross-cache move — the trickiest write. Touches BOTH the audio caches and the
// folder caches. The entire optimistic choreography (audio walk + folder-list
// counts + both folder details) is app-agnostic and now lives in
// @listening/frontend as `moveAudioToFolderEffect` + `useMoveAudioToFolderLifecycle`
// so mobile/browser-ext share it. The web app keeps only its own `onSuccess`
// UX (toast + log).
const useMoveSingleFileMutation = () => {
  return useMutation({
    ...eq.mutationOptions({
      mutationKey: [...foldersQueryKey, 'move-single-file'],
      mutationFn: (opts: { folderId: string; audioItem: AudioItemSummary }) =>
        moveAudioToFolderEffect(opts),
    }),
    ...useMoveAudioToFolderLifecycle(),
    onSuccess: (_, opts) => {
      moduleLogger.info(
        `Successfully moved ${opts.audioItem.id} to ${opts.folderId}`,
      );
      toast.success(
        m.toast_moved_audio({
          location:
            opts.folderId === 'root'
              ? m.common_home()
              : m.audio_choose_folder(),
        }),
      );
    },
  });
};

const useAddFilesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...eq.mutationOptions({
      mutationKey: [...foldersQueryKey, 'add-files'],
      mutationFn: (input: AddAudiosToFolderInput & { folderName: string }) =>
        addAudiosToFolderEffect(input),
    }),
    onSuccess: async (_, input) => {
      markAudiosFiled(queryClient, input.folderId, input.audioIds);
      await reconcileFolderAssignment(queryClient);
      moduleLogger.info(
        `Added ${input.audioIds.length} files to folder ${input.folderId}`,
      );
      toast.success(
        m.toast_added_files_to_folder({
          count: input.audioIds.length,
          folder: input.folderName,
        }),
      );
    },
    onError: () => {
      toast.error(m.files_add_failed());
    },
  });
};

export const folderService = {
  queries: {
    useListQuery,
  },
  mutations: {
    useCreateMutation,
    useUpdateMutation,
    useDeleteMutation,
    useMoveSingleFileMutation,
    useAddFilesMutation,
  },
  utils: {
    folderPathToString: (path: ReadonlyArray<string>) => path.join('/'),
  },
};
